import { useSyncExternalStore, type FormEvent, type KeyboardEvent, type ReactElement, type Ref } from "react";
import { FRAMES, MAX_CHAT_CHARS, MAX_NAME_PART_BYTES, fullName } from "odyssey-shared";
import { GlyphButton, centreOffset, logical } from "../../ui/kit/index.ts";
import { fieldFrame, frameClass } from "../../ui/kit/state.ts";
import type { NameMatch } from "./lookup.ts";
import { CHAT_BAR } from "./minimise.ts";
import { ENTRY_ROW } from "./picker.ts";
import { scopeIconClass, type ChatScope } from "./scope-icon.tsx";
import { ScopePicker } from "./scope-picker.tsx";
import type { ScopeTab } from "./tabs.ts";
import { say } from "../../words.ts";
import { pageKeyHints } from "../touch/key-hints.ts";

/**
 * The chat panel's entry row (P1.9-T40, ADR-068's amendment "the entry row as redrawn, and the scope picker"; the
 * design's chat panel, a visual reference): 16 logical px tall, the scope picker at the left, the field filling the
 * rest in its dark box and edge, and Send at the right, an icon button drawing the arrow and named Send, which
 * submits the row's form as Enter does, the pointer's path to sending; a press of the pointer on it keeps focus where
 * it was, so the next Enter opens the input again and never presses Send a second time. Under a whisper the field
 * names the reply's target before the text.
 *
 * In the minimised panel's bar (P1.9-T30, ADR-068's amendment "the panel minimises"; the design's `mintype` state) the
 * row is `compact`: the draft's scope icon in place of the picker, the field 12 tall and Send 10 square, each set in
 * the middle of the bar's content on whole logical pixels. While the bar shows its label the row is `away`: only the
 * field stays, kept off the screen but in the page, so Enter and Tab still reach the one input and focus is never
 * lost as the panel folds; taking focus brings the row back.
 */

/**
 * The most a draft may hold past a Local line's limit: room before it for a whisper's command and two names.
 */
const COMMAND_ROOM = "/whisper ".length + MAX_NAME_PART_BYTES + 1 + MAX_NAME_PART_BYTES + 1;

/**
 * The side of a scope icon as the atlas draws it.
 */
const ICON = 10;

/**
 * The compact row's height: the bar's content while it is an input, inside its frame's edge and padding.
 */
const COMPACT_HEIGHT = CHAT_BAR.height - FRAMES.hud.insets.top - FRAMES.hud.insets.bottom - 2 * CHAT_BAR.typingPadding;

/**
 * How far the compact field's text sits from the field's outer edge, the design's 1 px edge and 2 px of padding.
 */
const COMPACT_TEXT_INSET = 3;

/**
 * Where a part of the compact row sits down the row: in its middle, on whole logical pixels, which for a part taller
 * than the row is above its top.
 *
 * @param height The part's height in logical pixels.
 * @returns Its top margin.
 */
function compactTop(height: number): number {
  return centreOffset(COMPACT_HEIGHT, height);
}

/**
 * What the input tells the panel and what the panel tells it of the stepper's list, which the input owns.
 */
export interface EntryInput {
  /** Where the page keeps the input element, to focus it on Enter. */
  readonly inputRef: Ref<HTMLInputElement>;
  /** The draft. */
  readonly draft: string;
  /** Whether the input has focus. */
  readonly focused: boolean;
  /** Told the draft as it is edited. */
  readonly onDraft: (draft: string) => void;
  /** The input's keys. */
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Told when the input takes focus. */
  readonly onFocus: () => void;
  /** Told when the input loses focus. */
  readonly onBlur: () => void;
  /** The ids of the lines that describe the input. */
  readonly describedBy: string;
  /** The stepper's list's id while it shows, or null. */
  readonly listId: string | null;
  /** The highlighted entry's id while the list shows, or null. */
  readonly activeId: string | null;
  /** Whether the input stays out of the Tab order, reached only by the page's own focus call. */
  readonly reachedByPageOnly?: boolean;
}

/**
 * The row.
 *
 * @param props The `input`'s state and handlers; whether the connection speaks chat (`connected`); the `scope` the
 *   draft goes to, or null for a command; the `scopes` the picker offers and `onPick` with one chosen; whom a draft
 *   under a whisper replies to (`reply`: a player, `nobody`, or null where it is no such reply) and the id of the
 *   line that says so (`replyId`); `onSubmit`, run by Send and by the form; the `gap` above the row in logical px,
 *   over which the picker's list sits; `layer`, the picker's list's layer on the input router; and `shape`: `full`
 *   in the panel, `compact` in the minimised bar while it is an input, `away` while the bar shows its label; and
 *   `pickerDismissed`, a count the panel raises to close the picker's list.
 * @returns The form.
 */
export function EntryRow({ input, connected, scope, scopes, onPick, reply, replyId, onSubmit, gap, layer, shape = "full", pickerDismissed }: { readonly input: EntryInput; readonly connected: boolean; readonly scope: ChatScope | null; readonly scopes: readonly ScopeTab[]; readonly onPick: (scope: ScopeTab) => void; readonly reply: { readonly kind: "to"; readonly name: NameMatch } | { readonly kind: "nobody" } | null; readonly replyId: string; readonly onSubmit: () => void; readonly gap: number; readonly layer: (shown: boolean, onDismiss: () => void) => void; readonly shape?: "full" | "compact" | "away"; readonly pickerDismissed: number }): ReactElement {
  const keyed = useSyncExternalStore(pageKeyHints.subscribe, pageKeyHints.getSnapshot, pageKeyHints.getSnapshot);
  const frame = fieldFrame({ hover: false, pressed: false, focus: input.focused, disabled: !connected, error: false });
  const full = shape === "full";
  const padding = (full ? ENTRY_ROW.fieldTextInset : COMPACT_TEXT_INSET) - FRAMES.field.insets.left;
  const fieldHeight = full ? ENTRY_ROW.height : CHAT_BAR.field;
  /**
   * The form's submission, by Send or by the browser.
   *
   * @param event The submission.
   */
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit();
  };
  return (
    <form className={full ? "ot-chat__entry" : shape === "away" ? "ot-chat__entry ot-chat__entry--compact ot-chat__entry--away" : "ot-chat__entry ot-chat__entry--compact"} data-chat="entry" data-chat-entry={shape} onSubmit={submit} style={{ height: logical(full ? ENTRY_ROW.height : COMPACT_HEIGHT), gap: logical(full ? ENTRY_ROW.gap : CHAT_BAR.typingGap) }}>
      {full ? (
        <ScopePicker scope={scope} scopes={scopes} onPick={onPick} disabled={!connected} bottom={ENTRY_ROW.height + gap} layer={layer} dismissed={pickerDismissed} />
      ) : shape === "away" || scope === null ? null : (
        <span className={`ot-chat__entry-scope ${scopeIconClass(scope)}`} role="img" aria-label={say(`chat.scopes.${scope}`)} data-chat="scope" data-chat-scope={scope} style={{ marginTop: logical(compactTop(ICON)) }} />
      )}
      <span className={`ot-chat__box ${frameClass(frame)}`} style={{ height: logical(fieldHeight), marginTop: full ? undefined : logical(compactTop(fieldHeight)) }}>
        {reply === null ? null : (
          <span className="ot-chat__reply" id={replyId} data-chat="reply" style={{ paddingLeft: logical(padding) }}>
            {reply.kind === "to" ? say("chat.replyTarget", { name: fullName(reply.name.firstName, reply.name.familyName) }) : say("chat.replyNobody")}
          </span>
        )}
        <input
          ref={input.inputRef}
          className="ot-chat__input"
          data-chat="input"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={input.listId !== null}
          aria-controls={input.listId ?? undefined}
          aria-activedescendant={input.activeId ?? undefined}
          aria-describedby={input.describedBy}
          value={input.draft}
          maxLength={input.draft.trimStart().startsWith("/") ? MAX_CHAT_CHARS + COMMAND_ROOM : MAX_CHAT_CHARS}
          disabled={!connected}
          tabIndex={input.reachedByPageOnly === true ? -1 : undefined}
          spellCheck={false}
          autoComplete="off"
          aria-label={say("chat.input")}
          placeholder={say(keyed ? "chat.placeholder" : "chat.placeholderTouch")}
          onChange={(event) => input.onDraft(event.currentTarget.value)}
          onKeyDown={input.onKeyDown}
          onFocus={input.onFocus}
          onBlur={input.onBlur}
          style={{ padding: `0 ${logical(padding)}` }}
        />
      </span>
      {full ? (
        <GlyphButton glyph="arrow-right" label={say("chat.send")} size={ENTRY_ROW.send} type="submit" keepFocus disabled={!connected} data={{ "data-chat": "send" }} />
      ) : shape === "away" ? null : (
        <span className="ot-chat__entry-send" style={{ marginTop: logical(compactTop(CHAT_BAR.send)) }}>
          <GlyphButton glyph="arrow-right-small" label={say("chat.send")} size={CHAT_BAR.send} type="submit" keepFocus disabled={!connected} data={{ "data-chat": "send" }} />
        </span>
      )}
    </form>
  );
}
